// src/components/personal/sections/ClientsSection/utils/statusHelpers.ts
import { useTranslation } from 'react-i18next';
import { COLORS, COMPONENT_STYLES } from '@/theme';
import { ClientStatus } from '../types/types';

/**
 * Получение цветовых классов для статуса клиента из темы
 */
export const getStatusColor = (status: ClientStatus): string => {
  // Проверяем статус на соответствие доступным в теме
  if (status in COLORS.clientStatus) {
    return COLORS.clientStatus[status].bg + ' ' + COLORS.clientStatus[status].text;
  }
  return COLORS.clientStatus.cold.bg + ' ' + COLORS.clientStatus.cold.text; // Дефолтные цвета из темы
};

/**
 * Получение стиля бейджа для статуса из темы
 */
export const getStatusBadgeStyle = (status: ClientStatus): string => {
  return `${COMPONENT_STYLES.badge.base} ${COMPONENT_STYLES.badge[status]}`;
};

/**
 * Получение текстового представления статуса с поддержкой i18n
 */
export const getStatusText = (status: ClientStatus): string => {
  const { t } = useTranslation(['clients']);
  
  switch (status) {
    case 'cold': return t('statuses.cold');
    case 'new': return t('statuses.new');
    case 'rare': return t('statuses.rare');
    case 'regular': return t('statuses.regular');
    case 'loyal': return t('statuses.loyal');
    case 'lost': return t('statuses.lost');
  }
};

/**
 * Получение текстового представления статуса без хука useTranslation
 * Используется в контекстах, где хуки нельзя использовать
 */
export const getStatusTextStatic = (status: ClientStatus, t: (key: string) => string): string => {
  switch (status) {
    case 'cold': return t('statuses.cold');
    case 'new': return t('statuses.new');
    case 'rare': return t('statuses.rare');
    case 'regular': return t('statuses.regular');
    case 'loyal': return t('statuses.loyal');
    case 'lost': return t('statuses.lost');
  }
};

/**
 * Опции статусов для выпадающих списков с использованием i18n
 */
export const getStatusOptions = (t: (key: string) => string) => [
  { value: 'all', label: t('filters.all_statuses') },
  { value: 'cold', label: t('statuses.cold') },
  { value: 'new', label: t('statuses.new') },
  { value: 'rare', label: t('statuses.rare') },
  { value: 'regular', label: t('statuses.regular') },
  { value: 'loyal', label: t('statuses.loyal') },
  { value: 'lost', label: t('statuses.lost') }
];

/**
 * Функция для определения изменения статуса (стрелка вверх/вниз)
 */
export const getStatusChangeStyle = (change: 'upgrade' | 'downgrade' | null): string => {
  if (!change) return '';

  return change === 'upgrade'
    ? `${COLORS.status.success.text} ml-1`
    : `${COLORS.status.error.text} ml-1`;
};

/**
 * Получение полного стиля для статуса с учетом изменения
 */
export const getFullStatusStyle = (
  status: ClientStatus, 
  change: 'upgrade' | 'downgrade' | null
): string => {
  const baseStyle = getStatusBadgeStyle(status);
  const changeStyle = getStatusChangeStyle(change);
  return `${baseStyle} ${changeStyle}`.trim();
};